import React, { useEffect, useState } from "react";
import PlatiUpload from "./plati/PlatiUpload";
import axios from "axios";
import { API_URL, getDailyPlati, paymentPaid } from "../../constants";
import { DatePicker, Table, Empty } from "antd";
import { ReloadOutlined, DownloadOutlined } from "@ant-design/icons";
import moment from "moment-timezone";
import dayjs from "dayjs";
import useTableSearch from "../../hooks/useTableSearch";
import "./workingmonth.css";

const { RangePicker } = DatePicker;
const dateFormat = "DD.MM.YYYY";

const RANGE_PRESETS = [
  { label: "Azi", value: [dayjs().startOf("day"), dayjs().endOf("day")] },
  {
    label: "Ieri",
    value: [
      dayjs().subtract(1, "day").startOf("day"),
      dayjs().subtract(1, "day").endOf("day"),
    ],
  },
  { label: "Ultimele 7 zile", value: [dayjs().subtract(6, "day").startOf("day"), dayjs().endOf("day")] },
  { label: "Ultimele 30 zile", value: [dayjs().subtract(29, "day").startOf("day"), dayjs().endOf("day")] },
  { label: "Luna curentă", value: [dayjs().startOf("month"), dayjs().endOf("day")] },
  {
    label: "Luna trecută",
    value: [
      dayjs().subtract(1, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
  },
];

const paginationProps = {
  defaultPageSize: 20,
  pageSizeOptions: ["10", "20", "50", "100"],
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (t, r) => `${r[0]}–${r[1]} din ${t} înregistrări`,
};

const Plati = () => {
  const [table, setTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [serviceMap, setServiceMap] = useState({});
  const [minDate, setMinDate] = useState(
    localStorage.getItem("minDatePlati")
      ? new Date(localStorage.getItem("minDatePlati"))
      : new Date(new Date().setHours(0, 0, 0, 0))
  );
  const [maxDate, setMaxDate] = useState(
    localStorage.getItem("maxDatePlati")
      ? new Date(localStorage.getItem("maxDatePlati"))
      : new Date(new Date().setHours(23, 59, 59, 999))
  );
  const getColumnSearchProps = useTableSearch();

  const getTable = async (min = minDate, max = maxDate) => {
    setLoading(true);
    try {
      const res = await paymentPaid({ minDate: min, maxDate: max });
      if (res.data) {
        setTable(
          res.data.map((el) => ({
            key: el.id,
            CODID: el.Plati && el.Plati.length ? el.Plati[0].CODID : "",
            USERINFO: el.Plati && el.Plati.length ? el.Plati[0].USERINFO : "",
            ...el,
          }))
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getServices = async () => {
    try {
      const res = await axios.get(API_URL + "payment/services");
      const map = {};
      (res.data || []).forEach((s) => {
        map[s.CODSERV] = s.NAMESERVIC;
      });
      setServiceMap(map);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getTable();
    getServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2 && dates[0] && dates[1]) {
      const newStartDate = new Date(dates[0]);
      newStartDate.setHours(0, 0, 0, 0);
      const newEndDate = new Date(dates[1]);
      newEndDate.setHours(23, 59, 59, 999);
      localStorage.setItem("minDatePlati", newStartDate);
      localStorage.setItem("maxDatePlati", newEndDate);
      setMinDate(newStartDate);
      setMaxDate(newEndDate);
      getTable(newStartDate, newEndDate);
    }
  };

  const handleDownload = async () => {
    await getDailyPlati(minDate, maxDate);
  };

  const totalIncasat = table.reduce(
    (s, p) => s + parseFloat(p.Amount || 0),
    0
  );
  const totalPozitii = table.reduce((s, p) => s + (p.Plati || []).length, 0);

  const expandedRowRender = (info) => {
    const columns = [
      {
        title: "Cod ID",
        dataIndex: "CODID",
        key: "codid",
        sorter: (a, b) => a.CODID.localeCompare(b.CODID),
        ...getColumnSearchProps("CODID"),
      },
      {
        title: "Tipul de plată",
        dataIndex: "PAY_TYPE",
        key: "paytype",
        sorter: (a, b) => a.PAY_TYPE.localeCompare(b.PAY_TYPE),
        ...getColumnSearchProps("PAY_TYPE"),
      },
      {
        title: "Data plății",
        dataIndex: "PAY_DATE",
        key: "paydate",
        sorter: (a, b) => new Date(a.PAY_DATE) - new Date(b.PAY_DATE),
        render: (text) => (
          <span className="wm-log-time">
            {moment(text).format("DD.MM.YYYY, HH:mm")}
          </span>
        ),
        ...getColumnSearchProps("PAY_DATE"),
      },
      {
        title: "Info utilizator",
        dataIndex: "USERINFO",
        key: "userinfo",
        sorter: (a, b) => a.USERINFO.localeCompare(b.USERINFO),
        ...getColumnSearchProps("USERINFO"),
      },
      {
        title: "Serviciul",
        dataIndex: "CODSERV",
        key: "codserv",
        sorter: (a, b) => a.CODSERV - b.CODSERV,
        render: (v) =>
          serviceMap[v] ? (
            <span>
              {v}{" "}
              <span style={{ color: "#62625e" }}>({serviceMap[v]})</span>
            </span>
          ) : (
            v
          ),
        ...getColumnSearchProps("CODSERV"),
      },
      {
        title: "Suma",
        dataIndex: "SUMA",
        key: "sum",
        align: "right",
        sorter: (a, b) => a.SUMA - b.SUMA,
        render: (v) => (
          <span className="wm-log-time">{parseFloat(v).toFixed(2)} lei</span>
        ),
        ...getColumnSearchProps("SUMA"),
      },
    ];
    const data = (info.Plati || []).map((element) => ({
      key: element.id,
      ...element,
    }));
    return (
      <Table
        locale={{ emptyText: <Empty description={"Nu există date"} /> }}
        columns={columns}
        dataSource={data}
        pagination={false}
        size="small"
      />
    );
  };

  const columns = [
    {
      title: "ID extern",
      dataIndex: "external_id",
      key: "external_id",
      sorter: (a, b) => a.external_id.localeCompare(b.external_id),
      render: (v) => <span className="wm-log-email">{v}</span>,
      ...getColumnSearchProps("external_id"),
    },
    {
      title: "Paynet ID",
      dataIndex: "paynet_payment_id",
      key: "paynet_payment_id",
      sorter: (a, b) =>
        (a.paynet_payment_id || "").localeCompare(b.paynet_payment_id || ""),
      ...getColumnSearchProps("paynet_payment_id"),
    },
    {
      title: "CODID",
      dataIndex: "CODID",
      key: "CODID",
      sorter: (a, b) => (a.CODID || "").localeCompare(b.CODID || ""),
      ...getColumnSearchProps("CODID"),
    },
    {
      title: "Client",
      dataIndex: "USERINFO",
      key: "USERINFO",
      sorter: (a, b) => (a.USERINFO || "").localeCompare(b.USERINFO || ""),
      ...getColumnSearchProps("USERINFO"),
    },
    {
      title: "Suma",
      dataIndex: "Amount",
      key: "Amount",
      align: "right",
      sorter: (a, b) => parseFloat(a.Amount || 0) - parseFloat(b.Amount || 0),
      render: (v) => (
        <span className="wm-log-time">
          {v != null ? `${parseFloat(v).toFixed(2)} lei` : "—"}
        </span>
      ),
    },
    {
      title: "Data de plată",
      dataIndex: "status_date",
      key: "status_date",
      sorter: (a, b) => new Date(a.status_date) - new Date(b.status_date),
      defaultSortOrder: "descend",
      render: (text) => (
        <span className="wm-log-time">
          {moment(text).tz("Europe/Chisinau").format("DD.MM.YYYY, HH:mm")}
        </span>
      ),
    },
  ];

  return (
    <div className="wm-page">
      {/* ===== SUMAR ===== */}
      <div className="wm-stats" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <div className="wm-stat wm-stat-good">
          <div className="wm-stat-label">Plăți confirmate</div>
          <div className="wm-stat-value">{table.length}</div>
          <div className="wm-stat-meta">în intervalul selectat</div>
        </div>
        <div className="wm-stat wm-stat-good">
          <div className="wm-stat-label">Total încasat</div>
          <div className="wm-stat-value">{totalIncasat.toFixed(2)} lei</div>
          <div className="wm-stat-meta">prin Paynet</div>
        </div>
        <div className="wm-stat">
          <div className="wm-stat-label">Poziții achitate</div>
          <div className="wm-stat-value">{totalPozitii}</div>
          <div className="wm-stat-meta">servicii în plățile din interval</div>
        </div>
      </div>

      {/* ===== FILTRE + UPLOAD ===== */}
      <div className="wm-grid" style={{ gridTemplateColumns: "3fr 2fr", marginTop: 0 }}>
        <div className="wm-card">
          <h3 className="wm-card-title">Plăți confirmate prin Paynet</h3>
          <div className="wm-filters" style={{ marginBottom: 0 }}>
            <div className="wm-filter">
              <span className="wm-filter-label">Selectați intervalul de date</span>
              <RangePicker
                placeholder={["Data de începere", "Data finală"]}
                defaultValue={[
                  dayjs(moment(minDate).format(dateFormat), dateFormat),
                  dayjs(moment(maxDate).format(dateFormat), dateFormat),
                ]}
                format={dateFormat}
                presets={RANGE_PRESETS}
                onChange={handleDateRangeChange}
                allowClear={false}
              />
            </div>
            <div className="wm-filters-actions">
              <button
                type="button"
                className="wm-btn wm-btn-secondary wm-btn-sm"
                onClick={handleDownload}
              >
                <DownloadOutlined /> Descarcă DBF
              </button>
              <button
                type="button"
                className="wm-btn wm-btn-secondary wm-btn-sm"
                onClick={() => getTable()}
              >
                <ReloadOutlined /> Reîncarcă
              </button>
            </div>
          </div>
          <p className="wm-tab-desc" style={{ marginTop: 10, marginBottom: 0 }}>
            Tabelul se actualizează automat la schimbarea intervalului. Click pe
            „+” la un rând pentru detalierea pe servicii.
          </p>
        </div>
        <div className="wm-card">
          <h3 className="wm-card-title">Încărcare plăți (DBF)</h3>
          <PlatiUpload />
        </div>
      </div>

      {/* ===== TABEL ===== */}
      <div className="wm-card wm-history" style={{ marginTop: 14 }}>
        <Table
          locale={{ emptyText: <Empty description={"Nu există date în intervalul selectat"} /> }}
          expandable={{ expandedRowRender }}
          scroll={{ x: 900 }}
          columns={columns}
          dataSource={table}
          loading={loading}
          size="small"
          pagination={paginationProps}
        />
      </div>
    </div>
  );
};

export default Plati;
